import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoadingButtonComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NewContact, SendDepositRequest } from '../../sdk/sdk';
import { DepositService } from '../../services/deposit.service';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'app-send-deposit-request-modal',
  imports: [ReactiveFormsModule, LoadingButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './send-deposit-request-modal.component.html',
})
export class SendDepositRequestModalComponent {
  readonly activeModal = inject(NgbActiveModal);
  private readonly depositService = inject(DepositService);
  private readonly modalService = inject(ModalService);

  opportunityId!: string;
  customerId!: number;
  isSubmitting = false;

  readonly form = new FormGroup({
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    textDifferentPerson: new FormControl<boolean>(false, { nonNullable: true }),
    firstName: new FormControl<string>('', { nonNullable: true }),
    lastName: new FormControl<string>('', { nonNullable: true }),
    phoneNumber: new FormControl<string>('', { nonNullable: true }),
    email: new FormControl<string>('', { nonNullable: true }),
  });

  constructor() {
    this.form.controls.textDifferentPerson.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((on) => this.applyContactValidators(on));
  }

  private applyContactValidators(on: boolean): void {
    const { firstName, lastName, phoneNumber, email } = this.form.controls;
    if (on) {
      firstName.addValidators(Validators.required);
      lastName.addValidators(Validators.required);
      phoneNumber.addValidators(Validators.required);
      email.addValidators(Validators.email);
    } else {
      for (const control of [firstName, lastName, phoneNumber, email]) {
        control.clearValidators();
        control.setValue('');
      }
    }
    for (const control of [firstName, lastName, phoneNumber, email]) {
      control.updateValueAndValidity();
    }
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const value = this.form.getRawValue();
    const request = new SendDepositRequest({
      opportunityId: this.opportunityId,
      customerId: this.customerId,
      amount: value.amount!,
      newContact: value.textDifferentPerson
        ? new NewContact({
            firstName: value.firstName,
            lastName: value.lastName,
            phoneNumber: value.phoneNumber,
            email: value.email ? value.email : undefined,
          })
        : undefined,
    });

    this.depositService.sendDepositRequest(request).subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.activeModal.close(result);
      },
      error: (err: { status?: number; response?: string; detail?: string }) => {
        this.isSubmitting = false;
        this.activeModal.dismiss('error');
        this.modalService.openErrorModal('Deposit request failed', this.errorMessage(err));
      },
    });
  }

  private errorMessage(err: { status?: number; response?: string; detail?: string }): string {
    // 503 (Authvia/Jobs unavailable) and 502 (the Sales->Jobs call itself failed, e.g. a
    // bad service key) are both transient/infra faults the seller can only wait out.
    if (err?.status === 503 || err?.status === 502) {
      return 'Payments are temporarily unavailable. Please try again later.';
    }
    // A declared problem status (e.g. 422) makes the generated SDK throw the parsed ProblemDetails
    // object (carrying `.detail`); an undeclared status throws a SwaggerException carrying the raw
    // `.response` body. Prefer the object's detail, then fall back to parsing the raw body.
    const detail = err?.detail ?? this.parseDetail(err?.response);
    if (detail) {
      return detail;
    }
    return 'Something went wrong sending the deposit request. Please try again.';
  }

  private parseDetail(response?: string): string | undefined {
    if (!response) {
      return undefined;
    }
    try {
      const body = JSON.parse(response) as { detail?: string; errorCode?: string };
      return body.detail;
    } catch {
      return undefined;
    }
  }
}
